// #region Imports

import { type IEventEmitter, PureEventEmitter } from '@breadstone/mosaik-elements';
import type { IPlatformAdapter } from './Adapters/interfaces/IPlatformAdapter';
import { PlatformAdapterFactory } from './Adapters/PlatformAdapterFactory';
import type { ThemeMode, ThemeModeWithSystem } from './Utils/ThemeMode';

// #endregion

/**
 * Checks if the given value is a theme mode.
 *
 * @public
 */
export function isThemeMode(value: unknown): value is ThemeMode {
    return value === 'dark' || value === 'light';
}

/**
 * Reactive observer that applies and tracks the active theme on the document.
 *
 * @remarks
 * Manages the `theme` and `theme-mode` document attributes and emits events
 * when either changes. The `'system'` mode is resolved via the
 * {@link IPlatformAdapter}.
 *
 * @public
 */
export class ThemeObserver {

    // #region Fields

    private readonly _themeChanged: PureEventEmitter<string>;
    private readonly _themeModeChanged: PureEventEmitter<ThemeMode>;
    private readonly _platformAdapter: IPlatformAdapter;
    private _currentTheme: string | null;
    private _currentThemeMode: ThemeMode | null;
    private _unsubscribe?: () => void;

    // #region Ctor

    /**
     * Constructs a new instance of the `ThemeObserver` class.
     *
     * @param platformAdapter - Optional platform adapter. Auto-detects if not provided.
     * @public
     */
    public constructor(platformAdapter?: IPlatformAdapter) {
        this._themeChanged = new PureEventEmitter<string>();
        this._themeModeChanged = new PureEventEmitter<ThemeMode>();
        this._platformAdapter = platformAdapter ?? PlatformAdapterFactory.create();
        this._currentTheme = null;
        this._currentThemeMode = null;

        this.observe({
            themeChange: (theme) => this._themeChanged.emit(theme),
            themeModeChange: (themeMode) => this._themeModeChanged.emit(themeMode)
        });
    }

    // #endregion

    // #region Properties

    /**
     * Fires when the theme changes.
     *
     * @public
     * @readonly
     */
    public get themeChanged(): IEventEmitter<string> {
        return this._themeChanged;
    }

    /**
     * Fires when the theme mode changes.
     *
     * @public
     * @readonly
     */
    public get themeModeChanged(): IEventEmitter<ThemeMode> {
        return this._themeModeChanged;
    }

    // #endregion

    // #region Methods

    /**
     * Applies the given theme and theme mode to the document.
     *
     * @public
     * @param theme - The theme to apply (joy, cosmopolitan or memphis).
     * @param themeMode - The theme mode to apply.
     */
    public applyTheme(theme: string, themeMode: ThemeModeWithSystem): void {
        this._platformAdapter.setDocumentAttribute('theme', theme);

        switch (themeMode) {
            case 'dark':
            case 'light':
                this._platformAdapter.setDocumentAttribute('theme-mode', themeMode);
                break;
            case 'system':
            default: {
                const isDark = this._platformAdapter.matchMedia('(prefers-color-scheme: dark)');
                this._platformAdapter.setDocumentAttribute('theme-mode', isDark ? 'dark' : 'light');
                break;
            }
        }
    }

    /**
     * Stops observing document attribute changes and releases the underlying listener.
     *
     * @remarks
     * Safe to call multiple times — subsequent calls are no-ops.
     *
     * @public
     */
    public dispose(): void {
        if (this._unsubscribe) {
            this._unsubscribe();
            this._unsubscribe = undefined;
        }
    }

    private observe(cb: {
        themeChange: (theme: string) => void;
        themeModeChange: (themeMode: ThemeMode) => void;
    }): void {
        this._currentTheme = this._platformAdapter.getDocumentAttribute('theme');
        this._currentThemeMode = this._platformAdapter.getDocumentAttribute('theme-mode') as ThemeMode | null;

        this._unsubscribe = this._platformAdapter.observeDocumentAttributes(
            ['theme', 'theme-mode'],
            (attributeName, newValue) => {
                if (attributeName === 'theme' && newValue) {
                    const theme = newValue;

                    if (this._currentTheme !== theme) {
                        this._currentTheme = theme;
                        cb.themeChange(theme);
                    }
                }

                if (attributeName === 'theme-mode') {
                    if (newValue === null) {
                        this._currentThemeMode = null;
                        return;
                    }

                    if (!isThemeMode(newValue)) {
                        throw new Error(`Invalid theme mode: ${newValue}`);
                    }

                    if (this._currentThemeMode !== newValue) {
                        this._currentThemeMode = newValue;
                        cb.themeModeChange(newValue);
                    }
                }
            }
        );
    }

    // #endregion

}

/**
 * Global service locator for the singleton {@link ThemeObserver} instance.
 *
 * @remarks
 * A default instance is created automatically at module load time.
 * Call {@link ThemeObserverServiceLocator.set | set} to replace it (e.g. in tests
 * or SSR environments where a custom {@link IPlatformAdapter} is needed).
 *
 * @public
 */
export class ThemeObserverServiceLocator {

    // #region Fields

    private static _current: ThemeObserver | null = null;

    // #endregion

    // #region Properties

    public static get current(): ThemeObserver {
        if (!ThemeObserverServiceLocator.isSet()) {
            throw new Error('\'ThemeObserver\' not set.');
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return ThemeObserverServiceLocator._current!;
    }

    // #endregion

    // #region Methods

    public static isSet(): boolean {
        return ThemeObserverServiceLocator._current !== null;
    }

    public static set(current: ThemeObserver): void {
        ThemeObserverServiceLocator._current = current;
    }

    // #endregion

}

ThemeObserverServiceLocator.set(new ThemeObserver());
